import { createEffect, createEvent, type Event, sample } from 'effector'
import type { z } from 'zod'

import { $i18n, type Translation } from '@/shared/lib/i18n'

type FormValidationError = { field?: string; message: string }

export function createFormValidation<TFormValues, TError = FormValidationError>(params: {
    formSubmitted: Event<FormData>
    validationSchema: z.ZodType<TFormValues>
    mapError: (i18n: Translation, error: z.ZodError<unknown>) => TError
}) {
    const { formSubmitted, validationSchema, mapError } = params

    const formValidateFx = createEffect<{ data: FormData }, TFormValues, z.ZodError>(({ data }) => {
        const formPayload = Object.fromEntries(data)

        return validationSchema.parse(formPayload)
    })

    sample({
        clock: formSubmitted,
        fn: (data) => ({ data }),
        target: formValidateFx,
    })

    const formValidationFailed = createEvent<TError>()

    sample({
        clock: formValidateFx.failData,
        source: $i18n,
        fn: ({ i18n }, error) => mapError(i18n, error),
        target: formValidationFailed,
    })

    const formValidationSucceeded = createEvent<TFormValues>()

    sample({
        clock: formValidateFx.doneData,
        target: formValidationSucceeded,
    })

    return {
        formValidationFailed,
        formValidationSucceeded,
    }
}
